import {
    ActorKind,
    AuditLogChangeType,
    SourcePredicate,
} from "@common/types/auditLog";
import {
    ActivityProcessName,
    APIProcessName,
    SQSProcessName,
} from "@common/constants/process-names";
import {
    HistoryField,
    applyTransactionUpdate,
    checkAndMergeSimilarCounterparties,
    normalizeCategorizationForTheSameTransactions,
    PLATFORM_HISTORY_SOURCE_TYPES,
} from "@common/constants/history-source-types";
import {
    FIELD_UPDATE_SOURCE,
    INTEGRATION_FIELD_UPDATE_SOURCES,
} from "@common/constants";

export const ALL_INTEGRATION_SOURCE_TYPES: readonly string[] = [
    SQSProcessName.PLAID_INITIAL_TRANSACTION_IMPORT,
    SQSProcessName.PLAID_TRANSACTION_IMPORT,
    APIProcessName.PLAID_WEBHOOK_SYNC_UPDATES_AVAILABLE,
    APIProcessName.PLAID_WEBHOOK_DEFAULT_UPDATE,
    APIProcessName.PLAID_WEBHOOK_HISTORICAL_UPDATE,
    APIProcessName.PLAID_WEBHOOK_TRANSACTIONS_REMOVED,
    SQSProcessName.STRIPE_INVOICES_SYNC,
    SQSProcessName.STRIPE_TRANSACTIONS_SYNC,
    SQSProcessName.STRIPE_SYNC,
    SQSProcessName.PAYPAL_SYNC,
    SQSProcessName.MERCURY_ACCOUNTS_SYNC,
    SQSProcessName.GUSTO_ACCOUNT_SYNC,
    SQSProcessName.ADP_RUN_ACCOUNT_SYNC,
    SQSProcessName.RAMP_TRANSACTIONS_SYNC,
    SQSProcessName.RAMP_BILLS_SYNC,
    SQSProcessName.RAMP_ACCOUNTS_SYNC,
    SQSProcessName.BILLCOM_SYNC,
];

export const KICK_ACTIVITY_SOURCE_TYPES: readonly string[] = [
    ActivityProcessName.TRANSACTION_ENRICHMENT,
    ActivityProcessName.TRANSACTION_ENRICHMENT_NORMALIZATION,
    ActivityProcessName.TRANSACTION_ENRICHMENT_SIDE_EFFECTS,
    ActivityProcessName.TRANSACTION_COUNTERPARTY_ENRICHMENT,
    applyTransactionUpdate,
    normalizeCategorizationForTheSameTransactions,
    checkAndMergeSimilarCounterparties,
];

const KICK_NATIVE_SOURCE_TYPES: readonly string[] = [
    ...KICK_ACTIVITY_SOURCE_TYPES,
    SQSProcessName.TRANSACTION_TRANSFER_MATCHING,
];

export const RULE_SOURCE_TYPES: readonly string[] = [
    SQSProcessName.TRANSACTION_RULE_APPLICATION,
    ActivityProcessName.TRANSACTION_SPLIT_RULE_APPLICATION,
];

// `metadata.context.system` values a categorization rule writes when it
// updates a field from another process.
export const RULE_FIELD_UPDATE_SOURCES: readonly FIELD_UPDATE_SOURCE[] = [
    FIELD_UPDATE_SOURCE.CATEGORIZATION_RULE_CREATED,
    FIELD_UPDATE_SOURCE.CATEGORIZATION_RULE_TRIGGERED,
    FIELD_UPDATE_SOURCE.CATEGORIZATION_RULE_REVERSED,
];

export const INTEGRATION_OWNED_FIELDS: readonly string[] = [
    HistoryField.STATUS,
    HistoryField.AMOUNT,
];

// Update sources that represent an integration acting on its own data during
// sync (e.g. PayPal creating and immediately splitting a transaction). When a
// row carries one of these in `metadata.context.system`, the change was made by
// the integration itself and should be credited to it regardless of the field.
// Derived from the shared INTEGRATION_FIELD_UPDATE_SOURCES (category-owning
// integrations) plus the data-import integrations (Plaid, Gusto) that don't set
// categories but still own their synced rows.
export const INTEGRATION_SYSTEM_SOURCES: readonly FIELD_UPDATE_SOURCE[] = [
    ...INTEGRATION_FIELD_UPDATE_SOURCES,
    FIELD_UPDATE_SOURCE.PLAID_IMPORT,
    FIELD_UPDATE_SOURCE.GUSTO,
    FIELD_UPDATE_SOURCE.ADP_RUN,
];

const KICK_OWNED_INTEGRATION_FIELDS: readonly HistoryField[] = [
    HistoryField.CATEGORY_ID,
    HistoryField.COUNTERPARTY_ID,
    HistoryField.PAIRED_TRANSFER_ID,
    HistoryField.SPLIT_PARENT_ID,
    HistoryField.CLASS_ASSIGNMENTS,
];

export interface IntegrationActorSelection {
    sourceTypes: readonly string[];
    // The integration's own `FIELD_UPDATE_SOURCE`(s), used to match UPDATE
    // rows the integration performed on its own data during sync. Scoping this to
    // the queried integration (instead of all integration systems) prevents a row
    // stamped with a different integration's system from matching.
    systemSources: readonly FIELD_UPDATE_SOURCE[];
}

export function getSourcePredicatesForIntegration(
    integration: IntegrationActorSelection,
): SourcePredicate[] {
    const sourceTypes = [...integration.sourceTypes];
    if (sourceTypes.length === 0) {
        return [];
    }
    const predicates: SourcePredicate[] = [
        {
            sourceTypes,
            changeTypes: [AuditLogChangeType.CREATE],
        },
        {
            sourceTypes,
            changeTypes: [AuditLogChangeType.UPDATE],
            fieldsSubsetOf: [...INTEGRATION_OWNED_FIELDS],
        },
    ];
    const systemSources = [...integration.systemSources];
    if (systemSources.length > 0) {
        predicates.push({
            sourceTypes,
            changeTypes: [AuditLogChangeType.UPDATE],
            systemsAnyOf: systemSources,
        });
    }
    return predicates;
}

export function getSourcePredicatesForAgentKind(
    kind: ActorKind,
): SourcePredicate[] {
    if (kind === ActorKind.RULE) {
        return [{ sourceTypes: [...RULE_SOURCE_TYPES] }];
    }

    if (kind === ActorKind.ORGANIZATION) {
        return [{ sourceTypes: [...PLATFORM_HISTORY_SOURCE_TYPES] }];
    }

    if (kind === ActorKind.KICK) {
        const integrationFieldPredicates: SourcePredicate[] =
            KICK_OWNED_INTEGRATION_FIELDS.map((field) => ({
                sourceTypes: [...ALL_INTEGRATION_SOURCE_TYPES],
                changeTypes: [AuditLogChangeType.UPDATE],
                fieldsAllOf: [field],
                systemsNoneOf: [...INTEGRATION_SYSTEM_SOURCES],
            }));

        return [
            { sourceTypes: [...KICK_NATIVE_SOURCE_TYPES] },
            {
                sourceTypes: [...ALL_INTEGRATION_SOURCE_TYPES],
                changeTypes: [AuditLogChangeType.UPDATE],
                excludeFields: [...INTEGRATION_OWNED_FIELDS],
                systemsNoneOf: [...INTEGRATION_SYSTEM_SOURCES],
            },
            ...integrationFieldPredicates,
        ];
    }

    return [];
}
